import { formatCurrencyAmount } from '@liminal/utils/currency';
import { formatOrderDate } from '@liminal/utils/date';
import { payOptionsFor, stillPaidUsd, type PayLevel } from '@liminal/content/rewards';
import { RewardMerchant_Category, RewardMerchant_State } from '@/api/generated';
import type { ActiveWindow, RewardMerchant } from '@/api/types';

// Partner Rewards helpers for the admin pages (LIM-4273): resource ids,
// merchant identities, and the rate preview operators see while setting a
// partner's rates and Pay with LIL levels.

/** The places LIL amounts are priced to. Mirrors `amount_decimals` in the rewards service config. */
export const LIL_AMOUNT_DECIMALS = 2;

/**
 * The id at the end of a resource name: `rewardMerchants/{id}` → `{id}`, or
 * null when the name is not one of `collection`'s, so a budget's name is never
 * read as a merchant's id. A page shows a null as the bad reference it is.
 */
export function parseResourceId(name: string, collection: 'rewardMerchants' | 'rewardBudgets'): string | null {
  const prefix = `${collection}/`;
  const id = name.startsWith(prefix) ? name.slice(prefix.length) : '';
  return id === '' || id.includes('/') ? null : id;
}

/** {@link parseResourceId} for a name the page itself is navigating by: throws on a name of another collection. */
export function resourceId(name: string, collection: 'rewardMerchants' | 'rewardBudgets'): string {
  const id = parseResourceId(name, collection);
  if (id === null) {
    throw new Error(`"${name}" is not a ${collection} resource name`);
  }
  return id;
}

/** The fourteen digits a CNPJ is stored as, from what an operator pasted ("11.222.333/0001-81"). */
export function cnpjDigits(input: string): string {
  return input.replace(/\D/g, '');
}

/** Whether a CNPJ as typed holds exactly the fourteen digits Rewards stores. */
export function isCompleteCnpj(input: string): boolean {
  return cnpjDigits(input).length === 14;
}

/** A non-negative decimal as an integer and the number of places it is scaled by. */
interface ScaledDecimal {
  readonly units: bigint;
  readonly scale: number;
}

const DECIMAL_TEXT = /^(\d+)(?:\.(\d+))?$/;

/**
 * Reads a non-negative decimal such as "20.40" exactly. Returns null when the
 * text is not one, so a form can say the field is invalid; it never guesses.
 */
export function parseDecimal(text: string): ScaledDecimal | null {
  const match = DECIMAL_TEXT.exec(text.trim());
  if (!match) return null;
  const fraction = match[2] ?? '';
  return { units: BigInt(match[1] + fraction), scale: fraction.length };
}

/** Writes a scaled decimal in its shortest exact form: 2040.00 → "2040", 61.70 → "61.7". */
function renderDecimal({ units, scale }: ScaledDecimal): string {
  const digits = units.toString().padStart(scale + 1, '0');
  const whole = digits.slice(0, digits.length - scale);
  const fraction = digits.slice(digits.length - scale).replace(/0+$/, '');
  return fraction === '' ? whole : `${whole}.${fraction}`;
}

/** Multiplies two decimals and rounds the product to `places`, up or down, exactly. */
function multiplyRounded(a: ScaledDecimal, b: ScaledDecimal, places: number, rounding: 'up' | 'down'): ScaledDecimal {
  const units = a.units * b.units;
  const scale = a.scale + b.scale;
  if (scale <= places) {
    return { units: units * 10n ** BigInt(places - scale), scale: places };
  }
  const divisor = 10n ** BigInt(scale - places);
  const quotient = units / divisor;
  const roundUp = rounding === 'up' && units % divisor !== 0n;
  return { units: roundUp ? quotient + 1n : quotient, scale: places };
}

/** What a payment of a given size costs and earns at a partner's rates. */
export interface RatePreview {
  /** LIL a customer pays to have the payment covered. */
  readonly redeemLil: string;
  /** LIL a customer earns instead. */
  readonly earnLil: string;
}

/**
 * Prices a payment the way Rewards does: the redeem cost rounds up and the
 * earning rounds down, to LIL_AMOUNT_DECIMALS places. Returns null when any
 * input is not a decimal. Display only: the server prices every real payment.
 */
export function previewRates(paidUsd: string, redeemLilPerUsd: string, earnLilPerUsd: string): RatePreview | null {
  const paid = parseDecimal(paidUsd);
  const redeem = parseDecimal(redeemLilPerUsd);
  const earn = parseDecimal(earnLilPerUsd);
  if (!paid || !redeem || !earn) return null;
  return {
    redeemLil: renderDecimal(multiplyRounded(paid, redeem, LIL_AMOUNT_DECIMALS, 'up')),
    earnLil: renderDecimal(multiplyRounded(paid, earn, LIL_AMOUNT_DECIMALS, 'down')),
  };
}

// -- Pay with LIL levels (P-LIM-222): up to three USD levels on a partner's rates --

/** The levels the partner form asks for. The service takes one to three; the form wants all three. */
export const PAY_LEVEL_COUNT = 3;

/**
 * A level's LIL as Rewards prices it (`PayLevel.lil`): its USD at the redeem
 * rate, rounded up to a whole LIL. Null when either is not a decimal. Display
 * only: the server prices every level it writes.
 */
export function payLevelLil(usd: string, redeemLilPerUsd: string): string | null {
  const level = parseDecimal(usd);
  const redeem = parseDecimal(redeemLilPerUsd);
  if (!level || !redeem) return null;
  return renderDecimal(multiplyRounded(level, redeem, 0, 'up'));
}

/**
 * What is wrong with each of the partner form's levels, by row, or undefined
 * for a row that is right: each is a dollar amount above zero and above the
 * level before it, which the service also refuses otherwise.
 */
export function payLevelErrors(levels: readonly string[]): readonly (string | undefined)[] {
  return levels.map((text, index) => {
    if (text.trim() === '') return 'Enter the level in dollars, such as 10.';
    const usd = parseDecimal(text);
    if (!usd) return 'Must be a decimal, such as 10 or 12.50.';
    if (usd.units === 0n) return 'Must be more than $0.';
    const below = index === 0 ? null : parseDecimal(levels[index - 1]);
    if (below) {
      const scale = Math.max(usd.scale, below.scale);
      const units = (d: ScaledDecimal) => d.units * 10n ** BigInt(scale - d.scale);
      if (units(usd) <= units(below)) return `Must be more than Level ${index} ($${levels[index - 1].trim()}).`;
    }
    return undefined;
  });
}

/** A level as the rate preview shows it on an example payment. */
export interface PayLevelPreview {
  readonly position: number;
  readonly usd: string;
  /** What the level costs, in whole LIL. */
  readonly lil: string;
  /** What choosing it returns and leaves to pay on the payment, or null when the payment does not offer it. */
  readonly offer: { readonly usdBack: string; readonly stillPaid: string; readonly coversPayment: boolean } | null;
}

/**
 * The levels on an example payment, by the offer rule the app and the server
 * apply (`payOptionsFor`, shared from @liminal/content/rewards): every level
 * below the payment, then the first that reaches it, nothing above. Each
 * returns its USD, capped at the payment, and costs its own LIL. Every level
 * is listed; one not offered has a null offer. Null when the amount, the rate
 * or a level is not a decimal, or the levels are not above zero and rising:
 * the form marks those and the preview waits for them, so `payOptionsFor`,
 * which throws on them, is only ever handed a clean set. Display only.
 */
export function previewOptions(paidUsd: string, redeemLilPerUsd: string, levels: readonly string[]): readonly PayLevelPreview[] | null {
  const paid = paidUsd.trim();
  if (!parseDecimal(paid) || payLevelErrors(levels).some((error) => error !== undefined)) return null;
  const priced: PayLevel[] = [];
  for (const usd of levels) {
    const lil = payLevelLil(usd, redeemLilPerUsd);
    if (lil === null) return null;
    priced.push({ usd: usd.trim(), lil });
  }
  const options = payOptionsFor(paid, priced);
  return priced.map((level, index) => {
    const option = options[index];
    return {
      position: index + 1,
      usd: level.usd,
      lil: level.lil,
      offer: option
        ? { usdBack: option.usdBack, stillPaid: stillPaidUsd(paid, option.usdBack), coversPayment: option.coversPayment }
        : null,
    };
  });
}

/**
 * Groups a decimal's whole part in thousands without passing it through a
 * float: "2040" → "2,040", "1234567.5" → "1,234,567.5".
 */
export function groupDecimal(text: string): string {
  const [whole, fraction] = text.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fraction === undefined ? grouped : `${grouped}.${fraction}`;
}

/**
 * A budget's limit less what is spent and held, exactly, or null when a figure
 * from the server is not a decimal: a budget whose figures cannot be read shows
 * as unreadable, never as a believable remainder.
 */
export function budgetRemaining(limitUsd: string, spentUsd: string, reservedUsd: string): string | null {
  const figures = [limitUsd, spentUsd, reservedUsd].map(parseDecimal);
  if (figures.some((f) => f === null)) return null;
  const read = figures.filter((f): f is ScaledDecimal => f !== null);
  const scale = Math.max(...read.map((f) => f.scale));
  const [limit, spent, reserved] = read.map((f) => f.units * 10n ** BigInt(scale - f.scale));
  const remaining = limit - spent - reserved;
  const magnitude = renderDecimal({ units: remaining < 0n ? -remaining : remaining, scale });
  return remaining < 0n ? `-${magnitude}` : magnitude;
}

/** How much of a budget is spent and held, as percentages of its limit, for drawing its bar. */
export interface BudgetUsage {
  readonly spentPercent: number;
  readonly heldPercent: number;
}

/**
 * A budget's spent and held shares of its limit, exactly to a hundredth of a
 * percent and capped so the two never pass 100. Null when a figure is not a
 * decimal or the limit is zero: an unreadable budget draws no bar rather than
 * a believable one. Display only.
 */
export function budgetUsage(limitUsd: string, spentUsd: string, reservedUsd: string): BudgetUsage | null {
  const figures = [limitUsd, spentUsd, reservedUsd].map(parseDecimal);
  if (figures.some((f) => f === null)) return null;
  const read = figures.filter((f): f is ScaledDecimal => f !== null);
  const scale = Math.max(...read.map((f) => f.scale));
  const [limit, spent, reserved] = read.map((f) => f.units * 10n ** BigInt(scale - f.scale));
  if (limit === 0n) return null;
  // Basis points of the limit, spent first, then held in what spent leaves.
  const spentBp = (spent * 10000n) / limit;
  const cappedSpentBp = spentBp > 10000n ? 10000n : spentBp;
  const heldBp = (reserved * 10000n) / limit;
  const cappedHeldBp = heldBp > 10000n - cappedSpentBp ? 10000n - cappedSpentBp : heldBp;
  return { spentPercent: Number(cappedSpentBp) / 100, heldPercent: Number(cappedHeldBp) / 100 };
}

/**
 * The share of a payment a customer gets back if they earn, as a percentage
 * rounded down to one place: earning 5 LIL per USD when 100 LIL buys one USD
 * back is "5". Null when a rate is not a decimal or the redeem rate is zero.
 */
export function cashbackPercent(earnLilPerUsd: string, redeemLilPerUsd: string): string | null {
  const earn = parseDecimal(earnLilPerUsd);
  const redeem = parseDecimal(redeemLilPerUsd);
  if (!earn || !redeem || redeem.units === 0n) return null;
  const tenths = (earn.units * 10n ** BigInt(redeem.scale) * 1000n) / (redeem.units * 10n ** BigInt(earn.scale));
  return renderDecimal({ units: tenths, scale: 1 });
}

/** A CNPJ's fourteen digits as printed, "11.222.333/0001-81"; anything else as stored. */
export function formatCnpj(digits: string): string {
  const match = /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/.exec(digits);
  return match ? `${match[1]}.${match[2]}.${match[3]}/${match[4]}-${match[5]}` : digits;
}

/**
 * A moment as a `datetime-local` input shows it, in the operator's own
 * timezone: "2026-10-12T09:30". Empty for no moment.
 */
export function toLocalInput(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A `datetime-local` value as the RFC 3339 UTC moment it names. Undefined for an empty input. */
export function fromLocalInput(value: string): string | undefined {
  if (value === '') return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) throw new Error(`"${value}" is not a date and time`);
  return d.toISOString();
}

// -- The partner catalogue (LIM-4607): category, links, locations, windows --

/**
 * The wire value of a partner with no category. The generated enum drops the
 * proto zero value, but the gateway emits unpopulated fields, so a partner
 * added before categories arrives carrying it.
 */
export const CATEGORY_UNSET = 'CATEGORY_UNSPECIFIED';

const CATEGORIES: readonly string[] = Object.values(RewardMerchant_Category);

/** Whether a string is one of the generated categories (a Select hands back plain strings). */
export function isMerchantCategory(value: string): value is RewardMerchant_Category {
  return CATEGORIES.includes(value);
}

/**
 * A partner's category, or null for one added before categories. A value this
 * console does not know throws: it is a newer category than this build, and
 * showing the partner as uncategorised would invite an operator to "fix" it.
 */
export function merchantCategoryOf(category: string | undefined): RewardMerchant_Category | null {
  if (category === undefined || category === CATEGORY_UNSET) return null;
  if (!isMerchantCategory(category)) throw new Error(`unknown reward merchant category "${category}"`);
  return category;
}

/** A point on the map, in decimal degrees. Coordinates, not money: plain numbers are right here. */
export interface Coordinates {
  readonly latitude: number;
  readonly longitude: number;
}

const DEGREES = /^-?\d{1,3}(?:\.\d+)?$/;

/**
 * A latitude or longitude as typed, or null when the text is not a number in
 * range: latitude from -90 to 90, longitude from -180 to 180. An empty field
 * is null, never the 0 that `Number('')` would make of it.
 */
export function parseDegrees(text: string, axis: 'latitude' | 'longitude'): number | null {
  const trimmed = text.trim();
  if (!DEGREES.test(trimmed)) return null;
  const degrees = Number(trimmed);
  const limit = axis === 'latitude' ? 90 : 180;
  return degrees >= -limit && degrees <= limit ? degrees : null;
}

/**
 * "lat, lng" as Google Maps copies a dropped pin ("-23.561414, -46.655881"),
 * or null when the text is not such a pair, in range.
 */
export function parseCoordinates(text: string): Coordinates | null {
  const parts = text.split(',');
  if (parts.length !== 2) return null;
  const latitude = parseDegrees(parts[0], 'latitude');
  const longitude = parseDegrees(parts[1], 'longitude');
  return latitude === null || longitude === null ? null : { latitude, longitude };
}

/** Google's own domains: google.com, google.com.br, google.co.uk, google.de. Not google.example.com. */
const GOOGLE_HOST = /^(?:www\.)?google\.[a-z]{2,3}(?:\.[a-z]{2})?$/;
const GOOGLE_MAPS_HOST = /^maps\.google\.[a-z]{2,3}(?:\.[a-z]{2})?$/;

/**
 * Whether a link is on one of the Google Maps hosts the server's resolver
 * reads: google.<tld>/maps, maps.google.<tld>, maps.app.goo.gl and
 * goo.gl/maps. Anything else is an ordinary link and is not sent to it.
 */
export function isGoogleMapsLink(text: string): boolean {
  let url: URL;
  try {
    url = new URL(text.trim());
  } catch {
    return false;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
  const host = url.hostname.toLowerCase();
  const onMapsPath = url.pathname === '/maps' || url.pathname.startsWith('/maps/');
  if (host === 'maps.app.goo.gl') return true;
  if (host === 'goo.gl') return onMapsPath;
  if (GOOGLE_MAPS_HOST.test(host)) return true;
  return GOOGLE_HOST.test(host) && onMapsPath;
}

/** The mean Earth radius, in metres (IUGG). */
const EARTH_RADIUS_METRES = 6_371_008.8;

/** The great-circle distance between two points, in metres (haversine). */
export function distanceMetres(a: Coordinates, b: Coordinates): number {
  const radians = (degrees: number) => (degrees * Math.PI) / 180;
  const dLatitude = radians(b.latitude - a.latitude);
  const dLongitude = radians(b.longitude - a.longitude);
  const h =
    Math.sin(dLatitude / 2) ** 2 +
    Math.cos(radians(a.latitude)) * Math.cos(radians(b.latitude)) * Math.sin(dLongitude / 2) ** 2;
  return 2 * EARTH_RADIUS_METRES * Math.asin(Math.sqrt(h));
}

/** Two locations closer than this are one place: a Maps link for it adds no second row. */
export const SAME_PLACE_METRES = 25;

/** A Google Maps search for a point, for the "Open in Maps" check beside a location. */
export function mapsSearchUrl({ latitude, longitude }: Coordinates): string {
  return `https://www.google.com/maps/search/?${new URLSearchParams({ api: '1', query: `${latitude},${longitude}` })}`;
}

/** A moment from the server as epoch milliseconds. A malformed one throws rather than comparing as NaN. */
function instant(iso: string): number {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) throw new Error(`"${iso}" is not a moment`);
  return ms;
}

/** Whether a window is open at `now`: from its start, up to but not including its end, as the server reads it. */
export function isWindowOpen(span: ActiveWindow, now: number): boolean {
  return instant(span.startTime) <= now && now < instant(span.endTime);
}

/** The soonest window start after `now`, as the server sent it, or null when no window is still to open. */
export function nextWindowStart(windows: readonly ActiveWindow[], now: number): string | null {
  let next: string | null = null;
  for (const span of windows) {
    const start = instant(span.startTime);
    if (start > now && (next === null || start < instant(next))) next = span.startTime;
  }
  return next;
}

/** Where a partner stands for customers right now. statusHelpers names each one as a pill. */
export type MerchantAvailability = 'live' | 'scheduled' | 'between' | 'paused' | 'retired';

/**
 * Where a partner stands for customers at `now`, worked out here from its
 * state and its windows by the rule the server applies: an active partner is
 * live when it has no windows or `now` falls inside one. An active partner
 * outside its windows is Scheduled while none of them has closed, and Between
 * windows once one has. The server's own `live` is not read: a gateway that
 * does not send it yet would otherwise make every partner look hidden.
 */
export function merchantAvailability(
  merchant: Pick<RewardMerchant, 'name' | 'state' | 'activeWindows'>,
  now: number,
): MerchantAvailability {
  if (merchant.state === undefined) throw new Error(`reward merchant ${merchant.name} has no state`);
  if (merchant.state === RewardMerchant_State.STATE_RETIRED) return 'retired';
  if (merchant.state === RewardMerchant_State.STATE_PAUSED) return 'paused';
  const windows = merchant.activeWindows ?? [];
  if (windows.length === 0 || windows.some((span) => isWindowOpen(span, now))) return 'live';
  return windows.some((span) => instant(span.endTime) <= now) ? 'between' : 'scheduled';
}

const MERCHANT_STATES: readonly string[] = Object.values(RewardMerchant_State);

/** The partners list's filters as the operator set them, from the URL. Empty means any. */
export interface MerchantFilters {
  readonly category: string;
  readonly state: string;
}

/**
 * The AIP-160 filter the partners list sends. A category or state this
 * console does not know (a hand-edited URL) throws rather than quietly
 * listing every partner.
 */
export function merchantFilter(filters: MerchantFilters): string {
  const clauses: string[] = [];
  if (filters.category) {
    if (!isMerchantCategory(filters.category)) throw new Error(`"${filters.category}" is not a partner category`);
    clauses.push(`category = ${filters.category}`);
  }
  if (filters.state) {
    if (!MERCHANT_STATES.includes(filters.state)) throw new Error(`"${filters.state}" is not a partner state`);
    clauses.push(`state = ${filters.state}`);
  }
  return clauses.join(' AND ');
}

/** The payments list's filters as the operator set them. Empty means any. */
export interface PaymentFilters {
  readonly merchant: string;
  readonly state: string;
  /** The first day, as a `date` input value, in the operator's timezone. */
  readonly from: string;
  /** The last day, inclusive, as a `date` input value. */
  readonly to: string;
  /**
   * The Owed preset's cut-off, as an RFC 3339 UTC moment: only payments whose
   * redemption has not moved since before it. Empty when the preset is off.
   */
  readonly owedBefore: string;
}

/** A `date` input's day as the RFC 3339 UTC moment it starts, in the operator's timezone. */
function dayStart(day: string, addDays: number): string {
  const d = new Date(`${day}T00:00`);
  if (Number.isNaN(d.getTime())) throw new Error(`"${day}" is not a date`);
  d.setDate(d.getDate() + addDays);
  return d.toISOString();
}

/**
 * The AIP-160 filter the payments list sends: merchant, state, a settle
 * window whose last day is included, and the Owed preset's cut-off on when
 * the payment's redemption last moved.
 */
export function paymentFilter(filters: PaymentFilters): string {
  const clauses: string[] = [];
  if (filters.merchant) clauses.push(`merchant = "${filters.merchant}"`);
  if (filters.state) clauses.push(`state = ${filters.state}`);
  if (filters.from) clauses.push(`settle_time >= "${dayStart(filters.from, 0)}"`);
  if (filters.to) clauses.push(`settle_time < "${dayStart(filters.to, 1)}"`);
  if (filters.owedBefore) clauses.push(`redemption.update_time < "${filters.owedBefore}"`);
  return clauses.join(' AND ');
}

/**
 * Whole seconds in a protobuf Duration as its JSON carries it ("3600s",
 * "3600.5s"), or null when it is absent or not a duration: the Owed preset
 * then has no window to cut off at, rather than an invented one.
 */
export function durationSeconds(text: string | undefined): number | null {
  const match = /^(\d+)(?:\.\d+)?s$/.exec(text ?? '');
  return match ? Number(match[1]) : null;
}

/** When a budget admits choices, in a few words. */
export function budgetWindowText(start: string | undefined, end: string | undefined): string {
  if (start && end) return `${formatOrderDate(start)} – ${formatOrderDate(end)}`;
  if (start) return `From ${formatOrderDate(start)}`;
  if (end) return `Until ${formatOrderDate(end)}`;
  return 'Always';
}

/** A window in its largest whole unit: 3600 → "1 h", 600 → "10 min", 90 → "90 s". */
export function durationText(seconds: number): string {
  if (seconds % 3600 === 0) return `${seconds / 3600} h`;
  if (seconds % 60 === 0) return `${seconds / 60} min`;
  return `${seconds} s`;
}

/** Whether a decimal as text is more than zero, read off its digits rather than through a float. */
export function isPositive(amount: string | undefined): amount is string {
  return amount !== undefined && !amount.startsWith('-') && /[1-9]/.test(amount);
}

/** A USD figure from the server for display, or a dash when the server sent none. Never an invented $0.00. */
export function usdText(amount: string | undefined): string {
  return amount ? formatCurrencyAmount(amount, 'USD') : '—';
}
